<template>
    <div class="history-items">
        <div
            v-for="(item, index) in historyItems"
            :class="`history-item ${item.status === 'retractable' ? 'special' : ''}`"
            :key="index"
        >
            <div class="icon">
                <span :class="`far fa-${item.icon}`" v-if="item.icon"></span>
            </div>
            <div class="desc">
                <div class="info-label" v-if="item.time">{{ dateFormat(item.time) }}</div>
                <div class="text">
                    {{ item.action }}
                    {{ item.target ? `${item.preposition} ${item.target}` : '' }}
                    {{ item.by ? `by ${item.by}` : '' }}
                </div>
            </div>
            <div
                v-if="allowExpand && item.hiddenItems && item.hiddenItems.length"
                class="menu"
                @click="viewCollapsed(index)"
                :title="`See all ${item.hiddenItems.length} events`"
            >
                <i class="far fa-external-link"></i>
            </div>
            <template v-if="item.status === 'retractable' && mode === 'review'">
                <div class="menu">
                    <i class="far fa-ellipsis-v"></i>
                </div>
                <elem-dropdown triggerSelector=".history-item.special .menu" position="bottom-end">
                    <template v-slot:default="slotProps">
                        <selectable-list
                            :items="[
                                { name: 'Retract from recipient', value: 'retract', icon: 'ban' }
                            ]"
                            :onSelect="retractPackage"
                            :active="slotProps.isOpen"
                        />
                    </template>
                </elem-dropdown>
            </template>
        </div>

        <dialog-window
            v-if="allowExpand"
            :show="!!expandedItems"
            :closeOnClickOutside="true"
            @closed="expandedItems = null"
        >
            <template v-slot:header>
                View Collapsed Events
            </template>
            <template v-slot:body>
                <history-items v-if="expandedItems" :items="expandedItems" :allowExpand="false"/>
            </template>
            <template v-slot:footer>
                <button class="button primary full-width" @click="expandedItems = null">
                    OK
                </button>
            </template>
        </dialog-window>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import dateFormat from 'dateformat';
    import { constants } from '../../utils/constants';

    export default {
        name: 'HistoryItems',
        props: {
            items: {
                type: Array,
                default: null
            },
            allowExpand: {
                type: Boolean,
                default: true
            },
            mode: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                expandedItems: null
            };
        },
        computed: {
            ...mapGetters({
                history: 'package/history',
                users: 'org/users',
                appName: 'base/appName',
            }),
            historyItems() {
                // No need to set up items array if directly passed from parent
                if (this.items) return this.items;

                // Format & condense the raw package audit log into user-friendly events
                let items = [];
                let views = [];
                let lastStatusChangeIndex = -1;
                let sentTo = '';
                let ipNameMap = {};
                let ipEmailMap = {};
                let skipViewUserId = null;
                for (let item of this.history) {
                    // Check if the current sending-to person has changed
                    if (item.new_data && item.new_data.sending_to !== undefined) {
                        sentTo = item.new_data.sending_to;
                    }

                    if (item.action === 'create') {
                        // Package created by user or API
                        let by;
                        if (item.user_id) {
                            by = this.getUserName(item.user_id)
                        } else if (item.submitted_by_fname) {
                            by = ipNameMap[item.ip_address] = `${item.submitted_by_fname} ${item.submitted_by_lname}`;
                        } else if (item.submitted_by_email) {
                            by = ipEmailMap[item.ip_address] = item.submitted_by_email;
                        } else if (item.user_agent) {
                            by = 'filler';
                        } else {
                            by = 'API Request';
                        }
                        items.push({
                            action: 'Created',
                            by,
                            time: item.created_at,
                            icon: 'file-plus',
                        });
                        skipViewUserId = item.user_id; // Skip the first view if it matches the creator's user ID
                    } else if (item.action === 'view') {
                        // User or filler viewed this pkg (these get buffered and then collapsed in sections)
                        let by;
                        if (item.user_id) {
                            if (item.user_id === skipViewUserId) {
                                skipViewUserId = null;
                                continue;
                            }
                            by = this.getUserName(item.user_id);
                        } else {
                            by = ipNameMap[item.ip_address] || sentTo || ipEmailMap[item.ip_address] || 'filler';
                        }
                        views.push({
                            action: 'Viewed',
                            by,
                            time: item.ended_at,
                            icon: 'book-open',
                            ip_address: item.ip_address,
                            user_id: item.user_id,
                        });
                        skipViewUserId = null;
                    } else if (item.action === 'retract') {
                        // Package retracted from the filler
                        dumpViews();
                        lastStatusChangeIndex = items.push({
                            action: 'Retracted',
                            by: this.getUserName(item.user_id),
                            time: item.ended_at,
                            status: 'Retracted',
                            icon: 'ban',
                        }) - 1;
                    } else if (item.action === 'revision') {
                        // A new revision was saved, either user/filler draft, user edit, sent, or submitted
                        let by;
                        if (item.user_id) {
                            by = this.getUserName(item.user_id);
                        } else if (item.submitted_by_email) {
                            if (item.submitted_by_fname) {
                                by = ipNameMap[item.ip_address] = `${item.submitted_by_fname} ${item.submitted_by_lname}`;
                            } else {
                                by = ipEmailMap[item.ip_address] = item.submitted_by_email;
                                if (ipNameMap[item.ip_address]) {
                                    by = ipNameMap[item.ip_address];
                                }
                            }
                            updatePreceding();
                        } else {
                            by = 'filler';
                        }
                        dumpViews();
                        items.push({
                            action: 'Edited',
                            by,
                            time: item.ended_at,
                            icon: 'edit',
                            ip_address: item.ip_address,
                        });
                        if (item.new_data) {
                            if (item.new_data.status === 'Active') {
                                // Package was submitted in addition to being edited
                                lastStatusChangeIndex = items.push({
                                    action: 'Submitted',
                                    by,
                                    time: item.ended_at,
                                    status: item.new_data.status,
                                    icon: 'envelope',
                                }) - 1;
                            } else if (item.new_data.status === 'Pending') {
                                // Package was sent in addition to being edited
                                lastStatusChangeIndex = items.push({
                                    action: sentTo ? 'Sent' : 'Link created',
                                    preposition: 'to',
                                    target: sentTo,
                                    by,
                                    time: item.ended_at,
                                    status: item.new_data.status,
                                    icon: 'paper-plane',
                                }) - 1;
                            }
                        }
                    } else if (item.action === 'update') {
                        // Package data was updated without a revision being saved
                        if (item.new_data.status === 'Pending') {
                            // Package sent without changes
                            dumpViews();
                            lastStatusChangeIndex = items.push({
                                action: sentTo ? 'Sent' : 'Link created',
                                preposition: 'to',
                                target: sentTo,
                                by: this.getUserName(item.user_id),
                                time: item.ended_at,
                                status: item.new_data.status,
                                icon: 'paper-plane',
                            }) - 1;
                        } else if (item.new_data.status === 'Active') {
                            // Package submitted without changes
                            dumpViews();
                            lastStatusChangeIndex = items.push({
                                action: 'Submitted',
                                by: `${item.submitted_by_fname} ${item.submitted_by_lname}`,
                                time: item.ended_at,
                                status: item.new_data.status,
                                icon: 'envelope',
                            }) - 1;
                        } else if (item.new_data.is_archived !== undefined) {
                            // Package (un)archived
                            dumpViews();
                            lastStatusChangeIndex = items.push({
                                action: item.new_data.is_archived ? 'Archived' : 'Unarchived',
                                by: this.getUserName(item.user_id),
                                time: item.ended_at,
                                icon: item.new_data.is_archived ? 'archive' : 'inbox',
                            }) - 1;
                        }
                    }
                }
                dumpViews();

                let lastItem = lastStatusChangeIndex > -1 ? items[lastStatusChangeIndex] : null;
                if (lastItem && lastItem.status === 'Pending') {
                    items.push({
                        icon: 'history',
                        action: 'Pending',
                        preposition: 'with',
                        target: lastItem.target,
                        status: 'retractable',
                    });
                }

                return items;

                /**
                 * Helper functions
                 */
                function dumpViews() {
                    skipViewUserId = null;
                    if (!views.length) return;
                    
                    // Update all buffered views with the submitter's name if the IP address is known
                    for (let view of views) {
                        if (view.user_id) continue; // Don't mess with users' names
                        if (ipNameMap[view.ip_address]) {
                            view.by = ipNameMap[view.ip_address];
                        } else if (view.by === 'recipient' && ipEmailMap[view.ip_address]) {
                            view.by = ipEmailMap[view.ip_address];
                        }
                    }

                    let latestView;
                    if (views.length === 1) {
                        latestView = views[0];
                    } else {
                        // Show the latest filler view if exists, else show the latest user view
                        for (let i = views.length - 1; i >= 0; i--) {
                            if (!views[i].user_id) {
                                latestView = views[i];
                                break;
                            }
                        }
                        if (!latestView) latestView = views[views.length - 1];
                        latestView.hiddenItems = views;
                    }
                    items.push(latestView);
                    views = [];
                }

                function updatePreceding() {
                    // Update immediately preceding views' & edits' names if the IP address is known
                    let update = [];
                    for (let i = items.length - 1; i >= 0; i--) {
                        let item = items[i];
                        if (item.action !== 'Viewed' && item.action !== 'Edited') break;
                        update.push(item);
                        if (item.hiddenItems) {
                            update = update.concat(item.hiddenItems);
                        }
                    }
                    for (let item of update) {
                        if (item.user_id) continue; // Don't mess with users' names
                        if (ipNameMap[item.ip_address]) {
                            item.by = ipNameMap[item.ip_address];
                        } else if (item.by === 'recipient' && ipEmailMap[item.ip_address]) {
                            item.by = ipEmailMap[item.ip_address];
                        }
                    }
                }
            },
        },
        methods: {
            viewCollapsed(index) {
                let item = this.historyItems[index];
                this.expandedItems = item.hiddenItems;
            },
            async retractPackage() {
                await this.$store.dispatch('package/retract');
                await this.$store.dispatch('package/loadHistory');
            },
            dateFormat(date) {
                let d = new Date(date);
                let now = new Date();
                let format;
                if (
                    d.getFullYear() === now.getFullYear() &&
                    d.getMonth() === now.getMonth() &&
                    d.getDate() === now.getDate()
                ) {
                    format = '"Today", h:MMtt';
                } else {
                    format = `${constants.LONG_DATE_FORMAT}, h:MMtt`;
                }
                return dateFormat(date, format);
            },
            getUserName(userId) {
                return this.users[userId] ? this.users[userId].name : `${this.appName} Support`;
            },
        }
    }
</script>

<style lang="scss" scoped>
    .history-items {
        overflow: auto;
        flex-grow: 1;
    }

    .history-item {
        display: flex;
        align-items: center;
        padding: 0.35em 0.5em;
        margin-top: 0.25em;

        &.special {
            @include border;
            @include border-radius-large;
            background-color: $color-disabled-bg;
        }

        .icon {
            width: 22px;
            flex-shrink: 0;

            span {
                font-size: 1.1em;

                &.fa-book-open {
                    font-size: 0.95em;
                }

                &.fa-file-plus {
                    font-size: 1.3em;
                }
            }
        }

        .desc {
            flex-grow: 1;

            .text {
                overflow-wrap: anywhere;
            }
        }

        .menu {
            width: 15px;
            flex-shrink: 0;
            cursor: pointer;
            text-align: center;
            color: $color-black;

            &:hover {
                color: $color-label;
            }
        }
    }

    .info-label {
        font-size: 11px;
        line-height: 1.2em;
    }
</style>